<template>
  <v-container fluid class="timesheet-container pa-2 pa-sm-4">
    <v-card outlined class="timesheet-card">
      <!-- Header -->
      <v-toolbar flat dense class="timesheet-toolbar">
        <v-toolbar-title class="subtitle-1 font-weight-bold text-truncate">
          WORKING HOURS / BẢNG CÔNG
        </v-toolbar-title>
      </v-toolbar>

      <v-divider />

      <!-- Search -->
      <v-card-text class="search-section">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-menu
              v-model="monthMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290"
              min-width="290"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="displayMonth"
                  label="Tháng"
                  dense
                  outlined
                  readonly
                  hide-details
                  prepend-inner-icon="mdi-calendar-month"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>

              <v-date-picker
                v-model="monthPicker"
                type="month"
                no-title
                full-width
                @change="changeMonth"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="empNo"
              label="Mã Số"
              dense
              outlined
              hide-details
              clearable
              autocomplete="off"
              inputmode="text"
              :disabled="disabled"
              @keyup.enter="loadTimesheet"
            />
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-btn
              block
              depressed
              color="teal"
              class="search-button"
              min-height="44"
              :loading="loading"
              :disabled="loading"
              @click="loadTimesheet"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Tìm kiếm
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- Employee summary -->
      <v-card-text class="summary-section">
        <v-row dense align="center">
          <v-col cols="12" md="8">
            <div class="caption black--text text--darken-1">
              NHÂN VIÊN / CÔNG NHÂN
            </div>
            <div class="subtitle-1 font-weight-bold employee-name">
              {{ employeeName || 'Chưa xác định' }}
            </div>
            <div class="caption black--text">
              Mã Số: {{ empNo || '--' }}
            </div>
          </v-col>

          <v-col cols="6" md="2" class="metric-item text-center">
            <div class="caption black--text">TỔNG GIỜ THÁNG</div>
            <div class="headline font-weight-bold metric-value">
              {{ formatHour(monthTotalHour) }} h
            </div>
          </v-col>

          <v-col cols="6" md="2" class="metric-item text-center">
            <div class="caption black--text">TỔNG GIỜ LÀM THÊM</div>
            <div class="headline font-weight-bold metric-value">
              {{ formatHour(monthTotalOtHour) }} h
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- Timesheet -->
      <div class="timesheet-table-wrapper">
        <v-simple-table
          dense
          class="timesheet-table"
          :class="{ 'is-loading': loading }"
        >
          <thead>
            <tr>
              <th class="text-center day-column">Ngày</th>
              <th class="text-right hour-column">Giờ công</th>
              <th class="text-right ot-column">OT</th>
              <th class="text-center diligence-column">Dil</th>
              <th class="text-center action-column">Mở</th>
            </tr>
          </thead>

          <tbody>
            <template v-for="row in rows">
              <tr
                :key="row.id"
                :class="getDayRowClass(row)"
                class="timesheet-row"
              >
                <td class="text-center font-weight-bold">
                  {{ row.day }}
                </td>

                <td class="text-right">
                  {{ formatHour(row.totalHour) }}
                </td>

                <td class="text-right">
                  <span
                    v-if="Number(row.totalOtHour) > 0"
                    class="font-weight-bold"
                  >
                    {{ formatHour(row.totalOtHour) }}
                  </span>
                  <span v-else class="black--text">-</span>
                </td>

                <td class="diligence-cell text-center">
                  {{ row.diligence || '-' }}
                </td>

                <td class="text-center">
                  <v-btn
                    icon
                    small
                    class="expand-button"
                    :aria-label="
                      expandedDay === row.day
                        ? 'Đóng chi tiết ngày ' + row.day
                        : 'Mở chi tiết ngày ' + row.day
                    "
                    @click.stop="toggleRow(row.day)"
                  >
                    <v-icon>
                      {{
                        expandedDay === row.day
                          ? 'mdi-chevron-up'
                          : 'mdi-chevron-down'
                      }}
                    </v-icon>
                  </v-btn>
                </td>
              </tr>

              <!-- Detail row -->
              <tr
                v-if="expandedDay === row.day"
                :key="row.id + '-detail'"
                class="detail-table-row"
              >
                <td colspan="5" class="pa-0">
                  <v-card flat class="detail-card">
                    <v-card-subtitle
                      class="py-2 px-3 font-weight-bold detail-title"
                    >
                      Chi tiết giờ ngày {{ row.day }}
                    </v-card-subtitle>

                    <v-row dense class="detail-grid px-2 pb-2">
                      <v-col
                        v-for="item in wrkDivItems"
                        :key="item.code"
                        cols="6"
                        sm="3"
                      >
                        <div class="detail-box">
                          <div class="caption black--text text-truncate">
                            {{ item.label }}
                          </div>
                          <div class="subtitle-2 font-weight-bold">
                            {{ formatHour(row[item.field]) }} h
                          </div>
                          <div class="caption black--text">
                            {{ item.code }}
                          </div>
                        </div>
                      </v-col>
                    </v-row>

                    <v-divider />

                    <div class="pa-3 text-right detail-total">
                      <span class="caption black--text">
                        Tổng Overtime:
                      </span>
                      <span class="subtitle-2 font-weight-bold">
                        {{ formatHour(row.totalOtHour) }} h
                      </span>
                    </div>
                  </v-card>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && rows.length === 0">
              <td colspan="5" class="text-center empty-state">
                <v-icon small class="mr-1">mdi-calendar-remove</v-icon>
                Không có dữ liệu
              </td>
            </tr>

            <tr v-if="loading">
              <td colspan="5" class="text-center loading-state">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="28"
                />
                <div class="caption mt-2">Đang tải dữ liệu...</div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <v-divider />

      <!-- Footer summary -->
      <v-card-text class="footer-summary">
        <v-row dense>
          <v-col cols="6" sm="3">
            <div class="caption black--text">Ngày làm</div>
            <div class="subtitle-1 font-weight-bold">
              {{ workDayCount }}
            </div>
          </v-col>

          <v-col cols="6" sm="3">
            <div class="caption black--text">Ngày nghỉ</div>
            <div class="subtitle-1 font-weight-bold">
              {{ diligenceDayCount }}
            </div>
          </v-col>

          <v-col cols="6" sm="3">
            <div class="caption black--text">Tổng giờ</div>
            <div class="subtitle-1 font-weight-bold">
              {{ formatHour(monthTotalHour) }} h
            </div>
          </v-col>

          <v-col cols="6" sm="3">
            <div class="caption black--text">Tổng giờ làm thêm</div>
            <div class="subtitle-1 font-weight-bold">
              {{ formatHour(monthTotalOtHour) }} h
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { getv10a5000List } from '@/api/view/mis/10/v10a5000.js'
import { getAuthor, getGroup } from '@/api/view/mis/10/v10aAuthor.js'

export default {
  name: 'V10a5000Timesheet',

  data() {
    return {
      empNo: '',
      userId: '',
      deptCd: '%',
      grpCd: '%',

      disabled: false,
      useYn: null,

      monthPicker: this.getCurrentMonth(),
      monthMenu: false,

      employeeName: '',
      rows: [],
      loading: false,
      expandedDay: null,

      monthTotalHour: 0,
      monthTotalOtHour: 0,

      wrkDivItems: [
        { code: 'R', label: 'Regular', field: 'regularHour' },
        { code: 'O', label: 'Overtime', field: 'overtimeHour' },
        { code: 'N', label: 'Night', field: 'nightHour' },
        { code: 'H', label: 'Holiday', field: 'holidayHour' },
        { code: 'S', label: 'Sunday', field: 'sundayHour' },
        { code: 'A', label: 'Overtime Night', field: 'otNightHour' },
        {
          code: 'T',
          label: 'Overtime Night Sunday',
          field: 'otNightSunHour'
        }
      ]
    }
  },

  computed: {
    displayMonth() {
      return this.monthPicker || ''
    },

    workDayCount() {
      return this.rows.filter(row => Number(row.totalHour) > 0).length
    },

    diligenceDayCount() {
      return this.rows.filter(
        row => String(row.diligence || '').trim() !== ''
      ).length
    }
  },

  created() {
    this.userId = this.$store.state.user.userId
    this.empNo = this.userId

    this.author(this.userId)
    this.loadTimesheet()
  },

  methods: {
    getCurrentMonth() {
      const date = new Date()

      return (
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0')
      )
    },

    getDayRowClass(row) {
      const totalHour = Number(row.totalHour) || 0

      if (row.isHoliday && totalHour === 0) {
        return 'holiday-no-work'
      }

      if (row.isHoliday && totalHour > 0) {
        return 'holiday-work'
      }

      return ''
    },

    async author(empNo) {
      const params = {
        menuId: 'v10a2000',
        empNo,
        tabNo: '99',
        div: '1'
      }

      try {
        const authorRes = await getAuthor(params)

        if (authorRes && Array.isArray(authorRes.list)) {
          authorRes.list.forEach(item => {
            this.useYn = item.useYn
          })
        }

        const groupRes = await getGroup(params)

        if (groupRes && Array.isArray(groupRes.list)) {
          groupRes.list.forEach(item => {
            this.grpCd = item.grpCd
          })
        }

        if (this.useYn !== 'Y') {
          this.disabled = true
          this.empNo = empNo
        }
      } catch (error) {
        console.error('TIMESHEET AUTHOR ERROR:', error)

        // Keep the original behavior safe: if authorization cannot be
        // resolved, prevent editing another employee number.
        this.disabled = true
        this.empNo = empNo
      }
    },

    toWrkMon(value) {
      if (!value) return ''
      return String(value).replace('-', '')
    },

    changeMonth() {
      this.monthMenu = false
      this.loadTimesheet()
    },

    normalizeNumber(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }

      const number = Number(value)

      return Number.isFinite(number) ? number : 0
    },

    getField(obj, camel, upper) {
      if (!obj) return null

      if (obj[camel] !== undefined) {
        return obj[camel]
      }

      if (obj[upper] !== undefined) {
        return obj[upper]
      }

      return null
    },

    normalizeRow(item, index) {
      const totalHour = this.normalizeNumber(
        this.getField(item, 'totalHour', 'TOTALHOUR')
      )

      const totalOtHour = this.normalizeNumber(
        this.getField(item, 'totalOtHour', 'TOTALOTHOUR')
      )

      const isHoliday =
        Number(this.getField(item, 'isHoliday', 'ISHOLIDAY')) === 1

      const holidayDesc =
        this.getField(item, 'holidayDesc', 'HOLIDAYDESC') || ''

      return {
        id: index + 1,

        day: String(
          this.getField(item, 'day', 'DAY') ||
            String(index + 1).padStart(2, '0')
        ),

        regularHour: this.normalizeNumber(
          this.getField(item, 'regularHour', 'REGULARHOUR')
        ),

        overtimeHour: this.normalizeNumber(
          this.getField(item, 'overtimeHour', 'OVERTIMEHOUR')
        ),

        nightHour: this.normalizeNumber(
          this.getField(item, 'nightHour', 'NIGHTHOUR')
        ),

        holidayHour: this.normalizeNumber(
          this.getField(item, 'holidayHour', 'HOLIDAYHOUR')
        ),

        sundayHour: this.normalizeNumber(
          this.getField(item, 'sundayHour', 'SUNDAYHOUR')
        ),

        otNightHour: this.normalizeNumber(
          this.getField(item, 'otNightHour', 'OTNIGHTHOUR')
        ),

        otNightSunHour: this.normalizeNumber(
          this.getField(item, 'otNightSunHour', 'OTNIGHTSUNHOUR')
        ),

        totalHour,
        totalOtHour,

        diligence: this.getField(item, 'diligence', 'DILIGENCE') || '',

        isHoliday,
        holidayDesc
      }
    },

    async loadTimesheet() {
      if (this.loading) return

      this.loading = true
      this.expandedDay = null

      const params = {
        empNo: this.empNo,
        deptCd: this.deptCd || '%',
        grpCd: this.grpCd || '%',
        wrkMon: this.toWrkMon(this.monthPicker)
      }

      try {
        const res = await getv10a5000List(params)

        const apiList = Array.isArray(res)
          ? res
          : res && Array.isArray(res.list)
            ? res.list
            : res && Array.isArray(res.data)
              ? res.data
              : []

        this.rows = apiList.map((item, index) =>
          this.normalizeRow(item, index)
        )

        const first = apiList[0] || {}

        this.employeeName =
          this.getField(first, 'empName', 'EMPNAME') ||
          this.getField(first, 'vnm', 'VNM') ||
          ''

        const apiMonthTotalHour = this.getField(
          first,
          'monthTotalHour',
          'MONTHTOTALHOUR'
        )

        const apiMonthTotalOtHour = this.getField(
          first,
          'monthTotalOtHour',
          'MONTHTOTALOTHOUR'
        )

        this.monthTotalHour =
          apiMonthTotalHour !== null &&
          apiMonthTotalHour !== undefined
            ? this.normalizeNumber(apiMonthTotalHour)
            : this.rows.reduce(
                (sum, row) => sum + row.totalHour,
                0
              )

        this.monthTotalOtHour =
          apiMonthTotalOtHour !== null &&
          apiMonthTotalOtHour !== undefined
            ? this.normalizeNumber(apiMonthTotalOtHour)
            : this.rows.reduce(
                (sum, row) => sum + row.totalOtHour,
                0
              )

        if (!this.empNo && this.getField(first, 'empNo', 'EMPNO')) {
          this.empNo = this.getField(first, 'empNo', 'EMPNO')
        }
      } catch (error) {
        console.error('TIMESHEET ERROR:', error)

        this.rows = []
        this.employeeName = ''
        this.monthTotalHour = 0
        this.monthTotalOtHour = 0

        if (this.$toast) {
          this.$toast.error('Không lấy được dữ liệu giờ công')
        }
      } finally {
        this.loading = false
      }
    },

    toggleRow(day) {
      this.expandedDay =
        this.expandedDay === day ? null : day
    },

    formatHour(value) {
      return this.normalizeNumber(value).toFixed(1)
    }
  }
}
</script>

<style scoped>
/*
 * Responsive layout
 * Vue 2 + Vuetify 2
 * Desktop / Tablet / iPhone / iPad
 */

.timesheet-container {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;
}

.timesheet-card {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.timesheet-toolbar {
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
}

.timesheet-toolbar .v-toolbar__content {
  min-width: 0;
  width: 100%;
}

.timesheet-toolbar .v-toolbar__title {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* All form sections are fluid */
.search-section,
.summary-section,
.footer-summary {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.search-section .row,
.summary-section .row,
.footer-summary .row {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

.search-section .col,
.summary-section .col,
.footer-summary .col {
  min-width: 0;
}

.search-button {
  width: 100%;
  min-width: 0;
  min-height: 44px !important;
}

/* Employee name must never force the form wider */
.employee-name {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Summary metrics automatically fit their column */
.metric-item {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.metric-item .caption,
.metric-value {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*
 * Responsive table:
 * - fills available width when possible
 * - scrolls only when the viewport is too narrow
 */
.timesheet-table-wrapper {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  box-sizing: border-box;
}

.timesheet-table {
  width: 100%;
  max-width: 100%;
  min-width: 520px;
}

.timesheet-table table {
  width: 100%;
  min-width: 520px;
  max-width: none;
  table-layout: fixed;
  border-collapse: collapse;
}

.timesheet-table th,
.timesheet-table td {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  vertical-align: middle;
  -webkit-tap-highlight-color: transparent;
  overflow: hidden;
}

.timesheet-table thead th {
  white-space: nowrap;
  color: #000000 !important;
  font-weight: 600;
  height: 42px;
}

.timesheet-table tbody td {
  height: 44px;
}

.day-column {
  width: 12%;
}

.hour-column {
  width: 23%;
}

.ot-column {
  width: 18%;
}

.diligence-column {
  width: 27%;
}

.action-column {
  width: 20%;
}

.diligence-cell {
  text-align: center !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expand-button {
  width: 40px !important;
  height: 40px !important;
  margin: 0 auto;
}

/* Holiday */
.holiday-no-work {
  background-color: #fff3e0;
}

.holiday-no-work td:first-child {
  color: #c62828;
  font-weight: 700;
}

.holiday-work {
  background-color: #fff3e0;
}

/* Detail */
.detail-table-row td {
  width: 100%;
  max-width: 100%;
  padding: 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.detail-card {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  background-color: #fafafa !important;
  box-sizing: border-box;
}

.detail-grid {
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
}

.detail-grid .col {
  min-width: 0;
}

.detail-box {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 62px;
  padding: 9px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: #ffffff;
  box-sizing: border-box;
  overflow: hidden;
}

.detail-box .caption,
.detail-box .subtitle-2 {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-total {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

/* Empty/loading */
.empty-state,
.loading-state {
  height: 120px;
  min-height: 120px;
  vertical-align: middle !important;
}

/*
 * Prevent iOS Safari from enlarging text.
 * 16px is especially important for input controls.
 */
:global(html) {
  -webkit-text-size-adjust: 100%;
}

:global(body) {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  -webkit-tap-highlight-color: transparent;
}

:global(#app) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

/*
 * Tablet / iPad
 * Keep search controls on one row when there is enough width.
 */
@media (min-width: 601px) and (max-width: 1024px) {
  .timesheet-container {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .search-section .col-sm-4 {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }

  .metric-value {
    font-size: 22px !important;
    line-height: 30px;
  }
}

/*
 * Phones
 * Everything becomes fluid and stacked.
 */
@media (max-width: 600px) {
  .timesheet-container {
    width: 100%;
    max-width: 100%;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .timesheet-card {
    border-radius: 4px;
  }

  .timesheet-toolbar {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .timesheet-toolbar .v-toolbar__title {
    font-size: 14px !important;
    line-height: 20px;
  }

  .timesheet-card .v-card__text {
    padding: 12px !important;
  }

  /*
   * 16px prevents automatic zoom on iOS when focusing an input.
   */
  .timesheet-card .v-text-field input,
  .timesheet-card .v-select input,
  .timesheet-card textarea {
    font-size: 16px !important;
  }

  .search-button {
    min-height: 44px !important;
  }

  /*
   * Two summary metrics per row.
   * The Vuetify cols already provide the 6/12 behavior.
   */
  .metric-item {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }

  .metric-item .caption {
    font-size: 10px !important;
    line-height: 14px;
  }

  .metric-item .headline {
    font-size: 20px !important;
    line-height: 28px;
  }

  /*
   * The table must fit the phone width.
   * The wrapper remains available for Safari's overflow handling,
   * but the table itself is never wider than its parent.
   */
  .timesheet-table,
  .timesheet-table table {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
  }

  .timesheet-table thead th,
  .timesheet-table tbody td {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .detail-box {
    min-height: 64px;
    padding: 9px 8px;
  }

  .footer-summary .col {
    padding-top: 8px;
    padding-bottom: 8px;
  }
}

/*
 * Very small iPhones (320 / 375px)
 */
@media (max-width: 375px) {
  .timesheet-container {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .timesheet-card .v-card__text {
    padding: 10px !important;
  }

  .timesheet-toolbar .v-toolbar__title {
    font-size: 13px !important;
  }

  .metric-item .caption {
    font-size: 9px !important;
  }

  .metric-item .headline {
    font-size: 18px !important;
  }

  .detail-box {
    padding-left: 7px;
    padding-right: 7px;
  }
}

/*
 * Large desktop screens
 * Prevent the form from becoming excessively stretched.
 */
@media (min-width: 1440px) {
  .timesheet-container {
    max-width: 1440px;
  }
}

/* Hover only exists on mouse/trackpad devices */
@media (hover: hover) {
  .detail-box:hover {
    background-color: #f5f5f5;
  }
}
</style>
